import React, { FormEvent, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PUBLIC_APP_NAME } from '../config/publicBrand';
import './auth.css';

function Register(): React.JSX.Element {
    const navigate = useNavigate();
    const { user, refreshSession } = useAuth();
    const [searchParams] = useSearchParams();
    const initialMode = useMemo(() => {
        const mode = (searchParams.get('mode') || '').toLowerCase();
        return mode === 'employee' ? 'employee' : 'company';
    }, [searchParams]);

    const [mode, setMode] = useState<'company' | 'employee'>(initialMode);
    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            if (mode === 'company') {
                const res = await apiRequest<{ user: { role: string } }>('/api/auth/signup', {
                    method: 'POST',
                    json: { companyName, name, email, password }
                });
                await refreshSession();
                navigate(res.user.role === 'admin' ? '/admin/dashboard' : '/technician/dashboard');
                return;
            }

            if (!user || user.role !== 'admin') {
                throw new Error('Only an admin can add employees. Please sign in as an admin first.');
            }

            navigate('/admin/settings?tab=team');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Registration failed');
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <AppShell showNav={false} showFooter={false}>
            <div className="auth-page">
                <section className="auth-hero">
                    <p className="auth-kicker">{PUBLIC_APP_NAME}</p>
                    <h1 className="auth-title">{mode === 'company' ? 'Create your workspace.' : 'Add a team member.'}</h1>
                    <p className="auth-subtitle">
                        {mode === 'company'
                            ? 'Create a new company tenant and the first admin account.'
                            : 'Invite employees from Settings → Team. They receive an email to set up their account.'}
                    </p>
                </section>

                <section className="auth-panel">
                    <div className="auth-brand">
                        <img className="auth-brand-logo" src="/logo.svg" alt="Logo" />
                        <span>{PUBLIC_APP_NAME}</span>
                    </div>
                    <h2>{mode === 'company' ? 'Get started' : 'Invite a team member'}</h2>
                    <p className="auth-help">
                        {mode === 'company'
                            ? 'This will create a new tenant (company) for your SaaS account.'
                            : user?.role === 'admin'
                                ? 'Use the Team tab to send email invitations to new admins and technicians.'
                                : 'Sign in as an admin to invite team members.'}
                    </p>

                    <div style={{ display: 'grid', gap: '10px', marginBottom: '10px' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f2630' }}>Account type</label>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="radio"
                                    name="mode"
                                    value="company"
                                    checked={mode === 'company'}
                                    onChange={() => setMode('company')}
                                />
                                Company
                            </label>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="radio"
                                    name="mode"
                                    value="employee"
                                    checked={mode === 'employee'}
                                    onChange={() => setMode('employee')}
                                />
                                Employee
                            </label>
                        </div>
                    </div>

                    {mode === 'company' ? (
                    <form className="auth-form" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="companyName">Company Name</label>
                            <input
                                id="companyName"
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="ABC Fire Services"
                            />
                        </div>
                        <div>
                            <label htmlFor="name">Full Name</label>
                            <input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Frin Patel"
                            />
                        </div>
                        <div>
                            <label htmlFor="email">Work Email</label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@company.com"
                            />
                        </div>
                        <div>
                            <label htmlFor="password">Password</label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 8 characters"
                            />
                        </div>
                        {error && <p style={{ color: '#c13619' }}>{error}</p>}
                        <div className="auth-actions">
                            <button className="auth-btn auth-btn-primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Creating...' : 'Create company account'}
                            </button>
                            <button className="auth-btn auth-btn-secondary" type="button" onClick={() => navigate('/login')}>
                                Back to Sign in
                            </button>
                        </div>
                    </form>
                    ) : (
                    <div className="auth-form">
                        {error && <p style={{ color: '#c13619' }}>{error}</p>}
                        <div className="auth-actions">
                            <button
                                className="auth-btn auth-btn-primary"
                                type="button"
                                disabled={!user || user.role !== 'admin'}
                                onClick={() => navigate('/admin/settings?tab=team')}
                            >
                                Go to Team settings
                            </button>
                            <button className="auth-btn auth-btn-secondary" type="button" onClick={() => navigate('/login')}>
                                Back to Sign in
                            </button>
                        </div>
                    </div>
                    )}
                </section>
            </div>
        </AppShell>
    );
}

export default Register;
